'use client';

import { useEffect, useState, DragEvent, useContext } from 'react';

import { List } from '@/components/molecules';
import { FavoritesContext } from '@/lib/contexts/favouriting';
import { usePlayAll } from '@/lib/hooks/PlayAll/usePlayAll';
import { logger } from '@/lib/logger';
import { getRandomDefaultImage } from '@/lib/utils/getRandomImage';
import { cn } from '@/lib/utils/tailwind';

import { Item, ItemPrefix, ItemSuffix, Content } from './TrackListComponents';
import { ContentTypeVariations } from '../CardCarousel';

import type { PlaylistTrack, PersonalPlaylist } from '@/lib/api';

export const PersonalTrackList = ({
  mediaType,
  tracks,
  rawData,
  onReorder,
  reloadTracks,
}: {
  mediaType: ContentTypeVariations;
  tracks: PlaylistTrack[];
  rawData: PersonalPlaylist;
  onReorder: (newOrder: PlaylistTrack[]) => void;
  reloadTracks: () => void;
}) => {
  const localTracks = tracks;
  const [defaultImage, setDefaultImage] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const favoritesContext = useContext(FavoritesContext);
  if (!favoritesContext) {
    throw new Error('AudioHeader must be wrapped in provider');
  }
  const { removeTrackFromUserPlaylist, reorderPlaylistTracksAndStore } =
    favoritesContext;

  const { handleClick } = usePlayAll({
    mediaType,
    rawData,
    overriddenTrackList: localTracks,
  });

  useEffect(() => {
    setDefaultImage(getRandomDefaultImage());
  }, []);

  if (!defaultImage) return null;

  const handleDragStart = (event: DragEvent<HTMLDivElement>, index: number) => {
    setDragIndex(index);
    event.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = async (
    event: DragEvent<HTMLDivElement>,
    dropIndex: number,
  ) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) return;
    const newList = Array.from(localTracks);
    const [moved] = newList.splice(dragIndex, 1);
    newList.splice(dropIndex, 0, moved);
    setDragIndex(null);
    onReorder(newList);

    const songsOrder = newList.map(
      (track, index) => `${track.trackId}:${index + 1}`,
    );
    try {
      await reorderPlaylistTracksAndStore(rawData.playListId, songsOrder);
    } catch (error) {
      const typedError = error as Error & { digest?: string };
      logger.error(typedError.message, {
        source: 'nextjs-app-personaltracklist-reordertracks',
        digest: typedError.digest,
        stack: typedError.stack,
      });
    }
  };
  const handleDeleteTrackClick = async (trackId: number) => {
    try {
      await removeTrackFromUserPlaylist(Number(rawData.playListId), trackId);
      reloadTracks();
    } catch (error) {
      const typedError = error as Error & { digest?: string };
      logger.error(typedError.message, {
        source: 'nextjs-app-personaltracklist-deletetrack',
        digest: typedError.digest,
        stack: typedError.stack,
      });
    }
  };

  function hasDisplayArtist(
    actors: PlaylistTrack['track_metadata']['actors'],
  ): actors is PlaylistTrack['track_metadata']['actors'] {
    return (
      typeof actors === 'object' &&
      actors !== null &&
      'displayArtist' in actors &&
      typeof actors.displayArtist?.name === 'string'
    );
  }

  const getTopActor = (track: PlaylistTrack, idx: number): string => {
    if (hasDisplayArtist(track.track_metadata.actors)) {
      return track.track_metadata.actors.displayArtist.name;
    }
    if (
      'tracks' in rawData &&
      Array.isArray(rawData.tracks) &&
      rawData.tracks[idx] &&
      hasDisplayArtist(rawData.tracks[idx].track_metadata?.actors)
    ) {
      return rawData.tracks[idx].track_metadata.actors.displayArtist.name;
    }
    return track.actors?.[0]?.name || '';
  };

  return (
    <List.Root
      type="custom"
      className={cn('flex flex-col items-center space-y-0')}
    >
      {localTracks.map((track, index) => {
        const actors = track.actors || [];
        const artistTop = getTopActor(track, index);
        const artistBottom =
          actors
            .filter(actor => actor.name !== artistTop)
            .map(actor => actor.name)
            .join(', ') || '';
        const imageUrl =
          track?.track_metadata?.album?.imagePath || defaultImage;

        return (
          <div
            key={track.trackId}
            draggable
            onDragStart={e => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={e => handleDrop(e, index)}
            className="w-full"
          >
            <Item
              className={cn(
                'group cursor-grab items-start hover:border-b-1 hover:border-b-(--color-surface-primary) w-full px-0 min-w-0',
              )}
              prefix={
                <ItemPrefix
                  imageUrl={imageUrl}
                  title={track.shortName}
                  mediaType={mediaType}
                  rawData={rawData}
                  startIndex={index}
                  trackId={track.trackId}
                  trackList={localTracks}
                />
              }
              suffix={
                <ItemSuffix
                  id={track.trackId}
                  handleDelete={() => handleDeleteTrackClick(track.trackId)}
                  playlistId={Number(rawData.playListId)}
                  isPersonalPlaylistTrack={true}
                  onTrackRemoved={reloadTracks}
                />
              }
            >
              <Content
                title={track.shortName}
                artistTop={artistTop}
                artistBottom={artistBottom}
                onClick={event =>
                  handleClick(
                    event as unknown as React.MouseEvent<HTMLButtonElement>,
                    index,
                    track.trackId,
                  )
                }
              />
            </Item>
          </div>
        );
      })}
    </List.Root>
  );
};
